import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

// Cancel one occurrence, or this one plus every later occurrence of the same
// recurring class.
export type GroupClassCancelScope = 'single' | 'series';

interface CancelGroupClassScopeChoiceProps {
  value: GroupClassCancelScope;
  onChange: (scope: GroupClassCancelScope) => void;
  // How many classes "this and all following" would cancel, this one included.
  seriesRemaining: number;
}

/**
 * Shown inside the cancel confirmation whenever the class came from a recurring
 * rule and has later occurrences. Without the choice, organisers had to cancel
 * a batch one class at a time.
 */
export function CancelGroupClassScopeChoice({
  value,
  onChange,
  seriesRemaining,
}: CancelGroupClassScopeChoiceProps) {
  const { t } = useTranslation(['staff']);

  return (
    <div className="space-y-2" role="radiogroup">
      {(['single', 'series'] as const).map((scope) => (
        <button
          key={scope}
          type="button"
          role="radio"
          aria-checked={value === scope}
          onClick={() => onChange(scope)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border bg-surface px-3 py-2.5 text-left text-body-sm transition-colors',
            // Destructive choice, so the accent is the danger colour rather than
            // the brand one — it matches the confirm button below it.
            value === scope
              ? 'border-danger shadow-sm'
              : 'border-line hover:border-danger/40',
          )}
        >
          {/* The card stays white, so the dot is what carries the selection. */}
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
              value === scope ? 'border-danger' : 'border-line',
            )}
          >
            {value === scope && <span className="h-2 w-2 rounded-full bg-danger" />}
          </span>
          <span className={cn(value === scope ? 'font-medium text-ink' : 'text-ink')}>
            {scope === 'single'
              ? t('staff:availability.groupClass.cancelConfirm.scopeSingle', 'This class only')
              : t(
                  'staff:availability.groupClass.cancelConfirm.scopeSeries',
                  'This and all following classes ({{count}})',
                  { count: seriesRemaining },
                )}
          </span>
        </button>
      ))}
    </div>
  );
}
